import { useState, type ReactNode } from "react";
import { X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

interface DifficultyDefinitionsProps {
  variant?: "popover" | "panel";
  className?: string;
}

const definitions = [
  {
    level: "Knowledge",
    tagline: "Remember what was taught.",
    description: "Recall facts, terms, definitions, or basic procedures straight from the material.",
    example: "\"Which of the following is the definition of a noun?\"",
  },
  {
    level: "Understanding",
    tagline: "Make sense of it in your own words.",
    description: "Explain, summarize, classify, or compare ideas — the student has to translate the concept, not just repeat it.",
    example: "\"Which sentence best paraphrases the passage's main argument?\"",
  },
  {
    level: "Application+",
    tagline: "Use it in a new situation.",
    description: "Apply, analyze, evaluate, or create — the student takes the concept to an unfamiliar scenario or makes a judgment.",
    example: "\"A new species is discovered with these traits — which existing genus should it be placed in, and why?\"",
  },
];

function DefinitionsContent({ onClose }: { onClose?: () => void }) {
  return (
    <div className="space-y-4">
      {definitions.map((def, index) => {
        return (
          <div key={def.level} className="space-y-1">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-sm">{def.level}</div>
                <div className="text-xs font-medium italic text-foreground/80">{def.tagline}</div>
              </div>
              {index === 0 && onClose && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="h-5 w-5 p-0 hover:bg-muted -mt-0.5"
                >
                  <X className="w-3 h-3" />
                  <span className="sr-only">Close</span>
                </Button>
              )}
            </div>
            <div className="text-xs text-muted-foreground leading-relaxed">
              {def.description}
            </div>
            <div className="text-xs text-muted-foreground leading-relaxed">
              Example: {def.example}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SingleDefinition({ def }: { def: (typeof definitions)[number] }) {
  return (
    <div className="space-y-1">
      <div className="font-semibold text-sm">{def.level}</div>
      <div className="text-xs font-medium italic text-foreground/80">{def.tagline}</div>
      <div className="text-xs text-muted-foreground leading-relaxed">{def.description}</div>
      <div className="text-xs text-muted-foreground leading-relaxed">Example: {def.example}</div>
    </div>
  );
}

/**
 * Wraps a trigger (e.g. a column header) so clicking it shows that single
 * difficulty level's definition — the same content as "Understanding
 * difficulty levels", scoped to one level.
 */
export function DifficultyLevelInfo({
  level,
  children,
  className = "",
}: {
  level: string;
  children: ReactNode;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const def = definitions.find((d) => d.level === level);
  if (!def) return <>{children}</>;
  const testId = `difficulty-${level.toLowerCase().replace(/[^a-z]+/g, "-")}`;
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`text-left cursor-pointer underline decoration-dotted decoration-muted-foreground/60 underline-offset-4 hover:decoration-foreground ${className}`}
          data-testid={`button-${testId}`}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" data-testid={`popover-${testId}`}>
        <SingleDefinition def={def} />
      </PopoverContent>
    </Popover>
  );
}

export function DifficultyDefinitions({ variant = "popover", className = "" }: DifficultyDefinitionsProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (variant === "panel") {
    return (
      <div className={`p-4 bg-muted/30 rounded-lg ${className}`}>
        <DefinitionsContent />
      </div>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline p-0 h-auto ${className}`}
          data-testid="button-difficulty-info"
        >
          Understanding difficulty levels
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" data-testid="popover-difficulty-definitions">
        <DefinitionsContent onClose={() => setIsOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
